import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom'
import { useApis } from '../../api/useApiClient';
import { useNotification } from '../../context/NotificationContext';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';

import Form from '../Common/Form';
import LoadingDetailsView from '../Common/Skeletons/LoadingDetailsView';

const EmployeeForm = ({ editMode = false }) => {

    const { tenantApi } = useApis();
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    const [formData, setFormData] = useState({
        name: '',
        phoneNumber: '',
        status: 'ACTIVE',
    });
    const [loading, setLoading] = useState(false);

    // Load employee data if in edit mode
    const { id } = useParams(); // Get the employee ID from the URL parameters if provided (in edit mode)
    const loadFormData = async () => {
        setLoading(true);
        try {
            const res = await tenantApi(`/tenants/employees/${id}`);
            const employeeData = res.data;
            // Set this explicitly to send only the allowed fields to update. Otherwise API will reject with validation error
            setFormData({
                name: employeeData.name || '',
                phoneNumber: employeeData.phoneNumber || '',
                status: employeeData.status || 'ACTIVE',
            });
        } catch (error) {
            console.error("Error loading employee data", error);
            showNotification(error.message || "Failed to load employee details", 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (editMode) {
            loadFormData();
        }
    }, [editMode]);

    // --- end of loading employee data for editing



    // --- Handle input changes
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };


    // Show skeleton while loading initial data (if in edit mode)
    if (loading)
        return <LoadingDetailsView />


    return (
        <Box sx={{ padding: { xs: 2, sm: 3 }, maxWidth: '800px' }}>
            <Form
                resourceName='Employee'
                apiPath='/tenants/employees'
                formData={formData}
                editMode={editMode}
                onSubmit={() => {
                    navigate(-1);
                }}
                onCancel={() => {
                    navigate(-1);
                }}
            >
                <Stack spacing={3}>
                    {/* Name and Phone Group */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            fullWidth
                        />
                        <TextField
                            label="Phone number"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                            required
                            fullWidth
                            helperText="Format: +1234567890 or 0123456789"
                        />
                    </Stack>

                    {/* Status - only show in edit mode */}
                    {editMode && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                select
                                label="Status"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                fullWidth
                                sx={{ maxWidth: { sm: '50%' } }}
                            >
                                <MenuItem value="ACTIVE">Active</MenuItem>
                                <MenuItem value="INACTIVE">Inactive</MenuItem>
                                <MenuItem value="TERMINATED">Terminated</MenuItem>
                            </TextField>
                        </Stack>
                    )}
                </Stack>
            </Form>
        </Box>
    );
};

export default EmployeeForm;
